import React from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

interface LoadingOverlayProps {
  visible: boolean;
  message?: string;
  title?: string;
  size?: 'small' | 'medium' | 'large';
  type?: 'overlay' | 'inline' | 'skeleton';
  /** When true, overlay covers only the parent container instead of the full page */
  contentOnly?: boolean;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible,
  message = 'Please wait...',
  title = 'Loading',
  size = 'medium',
  type = 'overlay',
  contentOnly = false,
}) => {
  if (!visible) {
    return null;
  }

  const sizeMap = {
    small: 16,
    medium: 24,
    large: 32,
  };

  const spinnerSize = sizeMap[size];

  if (type === 'inline') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: spinnerSize, color: '#1890ff' }} spin />}
        />
        <span style={{ marginLeft: 12, fontSize: 14, color: '#666' }}>{message}</span>
      </div>
    );
  }

  if (type === 'skeleton') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: spinnerSize, color: '#1890ff' }} spin />}
          />
          <div style={{ marginTop: 16 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 500,
                color: '#262626',
                marginBottom: '8px',
              }}
            >
              {title}
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: '#8c8c8c',
              }}
            >
              {message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: contentOnly ? 'absolute' : 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: contentOnly ? 999 : 9999,
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '32px 40px',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: '240px',
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: spinnerSize,
                  color: '#1890ff',
                }}
                spin
              />
            }
          />
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 500,
            color: '#262626',
            marginBottom: '8px',
          }}
        >
          {title}
        </h3>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: '#8c8c8c',
            textAlign: 'center',
          }}
        >
          {message}
        </p>
      </div>
    </div>
  );
};

export default LoadingOverlay;
